import { Component, Input, Output, EventEmitter, ChangeDetectorRef, OnInit, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ApiService } from '../../../apiConnection/ApiService';
import { CustomModalComponent } from '../../../components/custom-modal/custom-modal.component';
import { Urls } from '../../../apiConnection/urls';
import { CustomInputComponent } from '@/components/custom-input/custom-input.component';
import { Utils } from '@/utils/utils';
import { LoadingComponent } from '@/components/loading/loading.component';
import { QuotientToPercentageDirective } from '@/directives/quotient-to-percentage.directive';
import { DecimalInputDirective } from '@/directives/decimal-input.directive';
import { CentToEurDisplayDirective } from '@/directives/cent-to-eur-display.directive';
import { ProviderFormComponent } from '@/pages/providers/provider-form/provider-form.component';
import { CategoryFormComponent } from '@/pages/categories/category-form/category-form.component';
import { ProductTypeFormComponent } from '@/pages/product-types/product-type-form/product-type-form.component';
import { CustomSelectComponent } from '@/components/custom-select/custom-select.component';
import { ProductVariationsComponent } from '../product-variations/product-variations.component';

// Fields a variation always takes from its parent product; mirrors erp-backend's
// Product::INHERITED_FIELDS (plus `name`, recomposed as "Padre - Rojo / M"). The backend
// overwrites them on save anyway, so they're just shown read-only here.
const VARIATION_INHERITED_CONTROLS = ['name', 'category_id', 'provider_id', 'measurement_id', 'product_type_id', 'tax_type', 'allergens'];

// The 14 EU-regulated allergens (Reglamento (UE) 1169/2011); codes must match erp-backend's
// ProductsController::ALLERGENS exactly, since it validates against the same list.
const ALLERGENS = [
  { value: 'gluten', label: 'Gluten' },
  { value: 'crustaceans', label: 'Crustáceos' },
  { value: 'eggs', label: 'Huevos' },
  { value: 'fish', label: 'Pescado' },
  { value: 'peanuts', label: 'Cacahuetes' },
  { value: 'soybeans', label: 'Soja' },
  { value: 'milk', label: 'Lácteos' },
  { value: 'nuts', label: 'Frutos de cáscara' },
  { value: 'celery', label: 'Apio' },
  { value: 'mustard', label: 'Mostaza' },
  { value: 'sesame', label: 'Sésamo' },
  { value: 'sulphites', label: 'Sulfitos' },
  { value: 'lupin', label: 'Altramuces' },
  { value: 'molluscs', label: 'Moluscos' },
];

@Component({
  selector: 'app-product-form',
  imports: [
    CustomModalComponent,
    ReactiveFormsModule,
    CustomInputComponent,
    CustomSelectComponent,
    LoadingComponent,
    QuotientToPercentageDirective,
    DecimalInputDirective,
    CentToEurDisplayDirective,
    ProductVariationsComponent,
  ],
  templateUrl: './product-form.component.html',
  styleUrl: './product-form.component.scss',
})
export class ProductFormComponent {
  @Input() id = 0;
  /** Hides the fields a restaurant-focused catalog doesn't need (code, provider, unit, invoice tariffs, measures, description, stock) - same product/save flow, just a shorter form. See product-restaurant-view.page.ts. */
  @Input() restaurantView = false;
  @Output() closeFunction = new EventEmitter<any>();
  @Output() acceptFunction = new EventEmitter<any>();

  /** Initial load (getById): the form isn't ready yet, so the modal stays hidden. Signals because the app is zoneless. */
  public loading = signal(false);
  /** Save in flight: overlay only, the modal stays mounted so nothing typed is lost if the save fails. */
  public saving = signal(false);

  public saveForm: FormGroup = new FormGroup({});
  public coeTaxDefault: any = null;
  public dropdowns: any = { categories: [], measurements: [], providers: [], taxes: [], productTypes: [] };

  public manualOverrides: { [key: string]: boolean } = { price_ticket: false };
  public tariffOverrides: boolean[] = [];

  public allergenOptions = ALLERGENS;

  private isInitialLoad = false;

  /** Set when editing a variation: its parent product ({id, name, code}). */
  public parentProduct: any = null;
  /** The product is variable (has variations): it's not sellable and its type can't change. */
  public hasVariations = false;
  /** product_type_id as stored in the backend - the one variation generation actually uses. */
  public savedProductTypeId: number | null = null;

  public providerFormComponent = ProviderFormComponent
  public categoryFormComponent = CategoryFormComponent
  public productTypeFormComponent = ProductTypeFormComponent

  private codeRetryCount = 0;

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.createForm();
    this.loadDropdowns();
    this.setupCalculations();

    if (this.id !== 0) {
      this.loading.set(true);
      this.getTariffs(() => {
        this.getById(this.id);
      });
    } else {
      this.getTariffs();
      this.getIdByCompany();
    }
  }

  createForm() {
    this.saveForm = new FormGroup({
      id_by_company: new FormControl(null),
      code: new FormControl('', [Validators.required]),
      name: new FormControl('', [Validators.required]),
      barcode: new FormControl(null),
      description: new FormControl(null),
      cost: new FormControl(0, [Validators.required, Validators.min(0)]),
      tax_type: new FormControl(1, [Validators.required]),
      price_ticket: new FormControl(0, [Validators.min(0)]),
      more_prices: new FormArray([]),
      stock: new FormControl(0),
      medium_price: new FormControl(0), // read only
      category_id: new FormControl(null, [Validators.required]),
      measurement_id: new FormControl(null, [Validators.required]),
      provider_id: new FormControl(null),
      product_type_id: new FormControl(null),
      allergens: new FormControl<string[]>([]),
      special_measures: new FormGroup({
        h: new FormControl(null),
        w: new FormControl(null),
        t: new FormControl(null)
      })
    });
  }

  setupCalculations() {
    this.saveForm.get('price_ticket')?.valueChanges.subscribe(val => {
      if (val === null || val === '' || val === 0) {
        this.manualOverrides['price_ticket'] = false;
      }
    });

    this.saveForm.get('tax_type')?.valueChanges.subscribe(() => this.calculateAllPrices());
  }

  // Ticket prices are tax-inclusive: cost × ticket tariff × (1 + VAT of the selected tax_type).
  get expectedTicketPrice(): number {
    const cost = this.saveForm.get('cost')?.value || 0;
    const ticketTariff = this.coeTaxDefault || 1;
    const taxConfig = this.dropdowns.taxes.find((t: any) => t.id == this.saveForm.get('tax_type')?.value);
    const taxPercentage = taxConfig?.quotient ? Number(taxConfig.quotient) : 0;
    return parseFloat((cost * ticketTariff * (1 + taxPercentage / 100)).toFixed(2));
  }

  calculateAllPrices() {
    if (this.isInitialLoad) return;

    const cost = this.saveForm.get('cost')?.value || 0;

    if (!this.manualOverrides['price_ticket']) {
      this.saveForm.get('price_ticket')?.setValue(this.expectedTicketPrice, { emitEvent: false });
    }

    this.tariffItems.controls.forEach((control, index) => {
      if (!this.tariffOverrides[index]) {
        const quotient = control.get('quotient')?.value || 0;
        const calculatedPrice = cost * quotient;
        control.get('price')?.setValue(parseFloat(calculatedPrice.toFixed(2)), { emitEvent: false });
      }
    });

    this.cdr.detectChanges();
  }

  markAsManual(field: string, index?: number) {
    const cost = this.saveForm.get('cost')?.value || 0;

    if (index !== undefined) {
      const control = this.tariffItems.at(index);
      const currentVal = control.get('price')?.value || 0;
      const quotient = control.get('quotient')?.value || 0;
      const expectedVal = parseFloat((cost * quotient).toFixed(2));

      this.tariffOverrides[index] = currentVal !== expectedVal && currentVal !== 0;
    } else {
      const currentVal = this.saveForm.get(field)?.value || 0;
      const expectedVal = this.expectedTicketPrice;

      this.manualOverrides[field] = currentVal !== expectedVal && currentVal !== 0;
    }
  }

  get tariffItems() {
    return this.saveForm.get('more_prices') as FormArray;
  }

  get selectedProductTypeAllowsAllergens(): boolean {
    const productTypeId = this.saveForm.get('product_type_id')?.value;
    const productType = this.dropdowns.productTypes.find((pt: any) => pt.id === productTypeId);
    return !!productType?.has_allergens;
  }

  addTariffRow(data: any = { id: 0, quotient: 0, price: 0 }) {
    const rowIndex = this.tariffItems.length;
    this.tariffOverrides[rowIndex] = false;

    const row = new FormGroup({
      id: new FormControl(data.id ?? 0),
      quotient: new FormControl(data.quotient ?? 0, [Validators.required]),
      price: new FormControl(data.price ?? 0, [Validators.required]),
    });

    // Suscripciones para cambios manuales posteriores
    row.get('price')?.valueChanges.subscribe(val => {
      if (val === null || val === '' || val === 0) {
        this.tariffOverrides[rowIndex] = false;
      }
    });

    this.tariffItems.push(row);
  }

  getTariffs(callback?: () => void) {
    this.apiService.getPetition(Urls.urls.companyTariffs).subscribe((data: any) => {
      if (data.invoice_tariffs) {
        this.tariffItems.clear();
        data.invoice_tariffs.forEach((item: any) => this.addTariffRow(item));
      }

      this.coeTaxDefault = data.ticket_tariff;

      if (callback) {
        callback();
      }

      this.cdr.detectChanges();
    });
  }

  getById(id: number) {
    this.loading.set(true);
    this.isInitialLoad = true;

    this.apiService.getById(Urls.urls.products, id).subscribe((data: any) => {
      const productPrices = data.more_prices ? JSON.parse(data.more_prices) : [];
      delete data.more_prices;

      // cost/price_ticket/more_prices[].price are stored as cents; this form works in euros internally.
      productPrices.forEach((item: any) => item.price = (item.price || 0) / 100);

      const special_measures = typeof data.special_measures == 'object'
        ? data.special_measures
        : JSON.parse(data.special_measures);

      const allergens = !data.allergens
        ? []
        : (typeof data.allergens == 'object' ? data.allergens : JSON.parse(data.allergens));

      const formData = {
        ...data,
        cost: (data.cost || 0) / 100,
        price_ticket: (data.price_ticket || 0) / 100,
        special_measures: special_measures || { h: null, w: null, t: null },
        allergens
      };

      productPrices.forEach((item: any) => {
        const existingControl = this.tariffItems.controls.find(
          control => control.get('id')?.value === item.id
        );

        if (existingControl) {
          existingControl.patchValue({
            quotient: item.quotient,
            price: item.price
          }, { emitEvent: false });
        } else {
          this.addTariffRow(item);
        }
      });

      this.saveForm.patchValue(formData);
      this.syncManualStates();

      this.parentProduct = data.parent_id ? (data.parent ?? { id: data.parent_id }) : null;
      this.savedProductTypeId = data.product_type_id ?? null;
      this.setHasVariations(!!data.has_variations);
      if (this.parentProduct) {
        VARIATION_INHERITED_CONTROLS.forEach(name => this.saveForm.get(name)?.disable({ emitEvent: false }));
      }

      this.isInitialLoad = false;
      this.loading.set(false);
      this.cdr.detectChanges();
    });
  }

  private setHasVariations(value: boolean) {
    this.hasVariations = value;
    // erp-backend rejects changing the type of a product that has variations.
    const productType = this.saveForm.get('product_type_id');
    value ? productType?.disable({ emitEvent: false }) : (!this.parentProduct && productType?.enable({ emitEvent: false }));
  }

  /** After variations are generated/deleted, refresh only has_variations - a full getById would drop unsaved edits. */
  onVariationsChanged() {
    this.apiService.getById(Urls.urls.products, this.id).subscribe((data: any) => {
      this.setHasVariations(!!data.has_variations);
      this.acceptFunction.emit();
      this.cdr.detectChanges();
    });
  }

  getIdByCompany() {
    this.apiService.getPetition(Urls.urls.products + '/last-id-by-company').subscribe((data: any) => {
      this.saveForm.patchValue({
        id_by_company: data.last_id_by_company + 1,
        code: String(data.last_id_by_company + 1).padStart(3, '0')
      });
      this.cdr.detectChanges();
    });
  }

  syncManualStates() {
    const cost = this.saveForm.get('cost')?.value || 0;

    // Valid ticket
    const currentPriceTax = this.saveForm.get('price_ticket')?.value || 0;
    const expectedPriceTax = this.expectedTicketPrice;

    if (currentPriceTax !== expectedPriceTax && currentPriceTax !== 0) {
      this.manualOverrides['price_ticket'] = true;
    }

    // Valid tariffs
    this.tariffItems.controls.forEach((control, i) => {
      const p = control.get('price')?.value || 0;
      const q = control.get('quotient')?.value || 0;
      const expectedP = parseFloat((cost * q).toFixed(2));

      if (p !== expectedP && p !== 0) {
        this.tariffOverrides[i] = true;
      }
    });
  }

  saveEntity() {
    if (this.saveForm.valid) {
      // getRawValue, not value: a variation's inherited controls are disabled, but the backend's
      // update validation still requires name/category_id/measurement_id (it then overwrites them
      // with the parent's anyway).
      const val = { ...this.saveForm.getRawValue() };
      // cost/price_ticket/more_prices[].price are edited in euros; convert to cents for storage.
      val.cost = Math.round((val.cost || 0) * 100);
      val.price_ticket = Math.round((val.price_ticket || 0) * 100);
      val.more_prices = JSON.stringify(
        (val.more_prices || []).map((item: any) => ({ ...item, price: Math.round((item.price || 0) * 100) }))
      );
      if (val.special_measures) {
        if ((val.special_measures.h ?? 0) + (val.special_measures.w ?? 0) + (val.special_measures.t ?? 0) == 0) {
          val.special_measures = null

        }
      }
      // Don't persist allergens picked while a product type allowed them if the product was
      // then switched to a type that no longer does.
      val.allergens = this.selectedProductTypeAllowsAllergens && val.allergens?.length ? val.allergens : null;

      this.saving.set(true);
      this.apiService.save(Urls.urls.products, val, this.id).subscribe({
        next: () => {
          Utils.notification.success("Producto guardado")
          this.codeRetryCount = 0;
          this.saving.set(false);
          this.acceptFunction.emit();
          this.closeFormFunction();
        },
        error: (error: any) => {
          if (Utils.isCodeAlreadyTakenError(error) && this.codeRetryCount < Utils.MAX_CODE_ALREADY_TAKEN_RETRIES) {
            this.codeRetryCount++;
            this.saveForm.get('code')?.setValue(Utils.incrementCode(this.saveForm.get('code')?.value));
            this.saveEntity();
            return;
          }

          this.codeRetryCount = 0;
          this.saving.set(false);
          const message = Utils.extractErrorMessage(error);
          // ProductsController rejects a duplicate reference with its own message (not Laravel's
          // "has already been taken"), so flag the field instead of silently renumbering it.
          if (message.toLowerCase().includes('código del producto ya existe')) {
            const codeControl = this.saveForm.get('code');
            codeControl?.setErrors({ codeTaken: true });
            codeControl?.markAsTouched();
          }
          Utils.notification.alert(message || "Error al guardar el producto")
          this.cdr.detectChanges();
        },
      });
    } else {
      Utils.notification.alert("Debe completar todos los campos obligatorios")
    }
  }

  loadDropdowns() {
    this.apiService.getSelectors(Urls.urls.selectors, { categories: {}, measurements: {}, providers: {}, companies: {}, product_types: {} }).subscribe((data: any) => {
      this.dropdowns.categories = data.categories || [];
      this.dropdowns.measurements = data.measurements || [];
      this.dropdowns.providers = data.providers || [];
      this.dropdowns.taxes = JSON.parse(data.companies[0].tax_config || '[]');;
      this.dropdowns.productTypes = data.product_types || [];

      // restaurantView hides the unit-of-measure selector (not relevant to a restaurant
      // catalog), but measurement_id is still required to save - default it silently.
      if (this.restaurantView && !this.saveForm.get('measurement_id')?.value) {
        this.saveForm.get('measurement_id')?.setValue(this.dropdowns.measurements[0]?.id ?? null);
      }

      this.cdr.detectChanges();
    });
  }

  closeFormFunction() { this.closeFunction.emit(); }
}